import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { ValueType } from 'react-select';
import moment from 'moment';
import ReactDatePicker from 'react-datepicker';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { Edit, FilterIcon } from '../ui/Icons';
import Modal from '../ui/Model';
import { LoadingData } from '../ui/LoadingData';
import { NotFoundData } from '../ui/NotFoundData';
import Tooltip from '../ui/Tooltip';
import { Pagination } from '../shared/components/core-ui/pagination/pagination.component';

import { getAllAreas, getAllCity } from '../shared/services/data/lookups.data';
import { getAllUnitTypes } from '../shared/services/data/unit-type.data';

import {
  createReservationOperationMaintenance,
  reservationsOperationsSearch,
  ReservationsOperationsItem,
  ReservationsOperationsQuery
} from './shared/services/operations.data';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import { UnitTypeUiModelResponse } from '../shared/ui-models/unit-type.ui-model';
import SortDropdown from './SortDropdown';
import ColumnVisibilityDropdown from './ColumnVisibilityDropdown';
import { birdToast } from '../shared/components/core-ui/toast';
import { APP_ROUTES, PUBLIC_PATH } from '../shared/config/routes-config';
import { RouteConfig } from '../shared/interfaces/routes-config.interface';
import {
  COLUMN_CONFIG,
  ColumnKey,
  COLUMNS_STORAGE_KEY,
  DEFAULT_VISIBLE_COLUMNS,
  formatCellDate,
  getRowTypeStyles,
  getStatusStyles,
  normalizeStatusLabel,
  selectMenuPortalStyles
} from './helpers';

function useColumnVisibility() {
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(() => {
    try {
      const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to load column preferences:', e);
    }
    return DEFAULT_VISIBLE_COLUMNS;
  });

  useEffect(() => {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key) && prev.length === 1) {
        return prev;
      }
      return prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
    });
  };

  const showAllColumns = () => setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);

  return { visibleColumns, toggleColumn, showAllColumns };
}

function Row({
  item,
  visibleColumns,
  onAddMaintenance
}: {
  item: ReservationsOperationsItem;
  visibleColumns: ColumnKey[];
  onAddMaintenance: (item: ReservationsOperationsItem) => void;
}) {
  const isBlocked = item.type === 'blockDate';
  const isReservation = item.type === 'reservation';

  const typeBadgeClass = isBlocked
    ? 'bg-purple-50 text-purple-700 border-purple-200'
    : isReservation
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-gray-50 text-neutral-600 border-gray-200';

  const statusText = isBlocked ? 'Blocked' : normalizeStatusLabel(item.status);
  const badgeClass = isBlocked
    ? 'bg-purple-100 text-purple-800 border-purple-200'
    : getStatusStyles(item.status);
  const reservationEditUrl = isReservation
    ? PUBLIC_PATH +
      (APP_ROUTES.RESERVATION_MODULE.CHILDREN as RouteConfig).EDIT_NEW.FULL_PATH.replace(
        ':id',
        String(item.id)
      )
    : undefined;

  const renderText = (val?: string | number | null) => {
    if (val === undefined || val === null || val === '') return '—';
    return String(val);
  };

  const showCol = (key: ColumnKey) => visibleColumns.includes(key);

  return (
    <tr
      className={`border-b border-gray-100 ${getRowTypeStyles(
        item.type,
        item.status,
        item.actualCheckOutAt
      )}`}
    >
      {showCol('reservationId') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle">
          <div className="flex items-center gap-2">
            <span className="text-base font-gilroy-medium text-neutral-600">{item.id}</span>
            {reservationEditUrl && (
              <a
                className="inline-flex items-center justify-center text-primary-500 "
                href={reservationEditUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Edit reservation ${item.id}`}
                title="Edit reservation"
              >
                <Edit size="16" />
              </a>
            )}
            <span
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-gilroy-medium uppercase tracking-wide ${typeBadgeClass}`}
            >
              {isBlocked ? 'Block' : isReservation ? 'Reservation' : item.type}
            </span>
          </div>
        </td>
      )}

      {showCol('area') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">
          {renderText(item.areaName)}
        </td>
      )}

      {showCol('project') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">
          {renderText(item.propertyName)}
        </td>
      )}

      {showCol('unitId') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle">
          <Tooltip title={<span className="text-base font-gilroy-medium">{item.roomId}</span>}>
            <span className="text-lg text-neutral-600 font-gilroy-medium">
              {renderText(item.roomName || item.roomId)}
            </span>
          </Tooltip>
        </td>
      )}

      {showCol('guestName') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">
          {renderText(item.primaryGuestName)}
        </td>
      )}

      {showCol('phoneNumber') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle">
          <span className="text-lg text-neutral-600 font-gilroy-medium">
            {renderText(item.primaryGuestPhone)}
          </span>
        </td>
      )}

      {showCol('checkIn') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">
          {formatCellDate(item.checkInDate)}
        </td>
      )}

      {showCol('checkOut') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">
          {formatCellDate(item.checkOutDate)}
        </td>
      )}

      {showCol('nights') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-center">
          <span className="inline-flex items-center justify-center min-w-8 h-8 rounded-full bg-gray-100 text-neutral-600 text-lg font-gilroy-medium px-2">
            {item.numberOfNights ?? '—'}
          </span>
        </td>
      )}

      {showCol('status') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle">
          <span
            className={`inline-flex items-center rounded-md border px-2 py-0.5 text-sm font-gilroy-medium uppercase tracking-wide ${badgeClass}`}
          >
            {statusText}
          </span>
        </td>
      )}

      {showCol('source') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">
          {renderText(item.source)}
        </td>
      )}

      <td className="px-3 py-2.5 whitespace-nowrap align-middle">
        <Button
          className="w-full"
          size={ButtonSize.small}
          type={ButtonType.primaryInverted}
          classNameButton="w-full px-4 py-2 text-sm leading-4"
          onClick={() => onAddMaintenance(item)}
        >
          Add Maintenance
        </Button>
      </td>

      {/* {showCol('guestType') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">—</td>
      )}
      {showCol('notes') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">—</td>
      )}
      {showCol('insurance') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">—</td>
      )}
      {showCol('cards') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">—</td>
      )}
      {showCol('cardsAmount') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">—</td>
      )}
      {showCol('cardsInsurance') && (
        <td className="px-3 py-2.5 whitespace-nowrap align-middle text-lg text-neutral-600">—</td>
      )} */}
    </tr>
  );
}

export default function OperationContainer() {
  const [loadingData, setLoadingData] = useState(false);
  const [items, setItems] = useState<ReservationsOperationsItem[]>([]);

  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(50);
  const [listCount, setListCount] = useState(0);

  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [cityId, setCityId] = useState<string>('');
  const [areaId, setAreaId] = useState<string>('');
  const [unitTypeId, setUnitTypeId] = useState<number | undefined>(undefined);
  const [propertyManagerId, setPropertyManagerId] = useState<string>('');
  const [statusList, setStatusList] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);

  const [sortBy, setSortBy] = useState<'checkInDate' | 'checkOutDate' | undefined>(undefined);
  const [appliedQuery, setAppliedQuery] = useState<ReservationsOperationsQuery>({});
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [selectedMaintenanceItem, setSelectedMaintenanceItem] =
    useState<ReservationsOperationsItem | null>(null);
  const [maintenanceDescription, setMaintenanceDescription] = useState('');
  const [submittingMaintenance, setSubmittingMaintenance] = useState(false);
  const maintenanceTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { visibleColumns, toggleColumn, showAllColumns } = useColumnVisibility();

  const [citiesOptions, setCitiesOptions] = useState<{ value: string; label: string }[]>([]);
  const [areaOptions, setAreaOptions] = useState<{ value: string; label: string }[]>([]);
  const [unitTypeOptions, setUnitTypeOptions] = useState<{ value: number; label: string }[]>([]);

  const statusOptions = useMemo(
    () =>
      [
        'confirmation_pending',
        'pending_check_in',
        'checked_in_started',
        'checked_in',
        'in_house',
        'checked_out',
        'canceled',
        'expired'
      ].map((s) => ({ value: s, label: s.replaceAll('_', ' ') })),
    []
  );

  // Build filter tags array
  const filterTags = useMemo(() => {
    const tags: { label: string; value: string }[] = [];

    if (appliedQuery.cityId) {
      const city = citiesOptions.find((c) => c.value === appliedQuery.cityId);
      if (city) tags.push({ label: 'City', value: city.label });
    }

    if (appliedQuery.areaId) {
      const area = areaOptions.find((a) => a.value === appliedQuery.areaId);
      if (area) tags.push({ label: 'Area', value: area.label });
    }

    if (appliedQuery.unitTypeId) {
      const unitType = unitTypeOptions.find((u) => u.value === appliedQuery.unitTypeId);
      if (unitType) tags.push({ label: 'Unit Type', value: unitType.label });
    }

    if (appliedQuery.status && (appliedQuery.status as string[]).length > 0) {
      const statusLabels = (appliedQuery.status as string[])
        .map((s) => statusOptions.find((opt) => opt.value === s)?.label || s)
        .join(', ');
      tags.push({ label: 'Status', value: statusLabels });
    }

    if (appliedQuery.fromDate) {
      tags.push({ label: 'From', value: moment(appliedQuery.fromDate).format('MM/DD/YYYY') });
    }

    if (appliedQuery.toDate) {
      tags.push({ label: 'To', value: moment(appliedQuery.toDate).format('MM/DD/YYYY') });
    }

    return tags;
  }, [appliedQuery, citiesOptions, areaOptions, unitTypeOptions, statusOptions]);

  // Check if any filters are applied
  const hasAppliedFilters = filterTags.length > 0;

  const load = React.useCallback(
    (page = offset, perPage = limit, query = appliedQuery) => {
      setLoadingData(true);
      reservationsOperationsSearch(query, page, perPage).then((res) => {
        setLoadingData(false);
        if (isErrorInfo(res)) {
          setItems([]);
          setListCount(0);
          return;
        }

        setItems(res.data || []);
        setListCount(res.count || 0);
      });
    },
    [appliedQuery, limit, offset]
  );

  const buildDraftQuery = (): ReservationsOperationsQuery => {
    return {
      ...(cityId ? { cityId } : {}),
      ...(areaId ? { areaId } : {}),
      ...(unitTypeId ? { unitTypeId } : {}),
      ...(propertyManagerId ? { propertyManagerId } : {}),
      ...(statusList.length ? { status: statusList } : {}),
      ...(fromDate ? { fromDate: fromDate.getTime() } : {}),
      ...(toDate ? { toDate: toDate.getTime() } : {}),
      ...(sortBy ? { sortBy } : {})
    };
  };

  const clearFilters = () => {
    setCityId('');
    setAreaId('');
    setUnitTypeId(undefined);
    setPropertyManagerId('');
    setStatusList([]);
    setFromDate(undefined);
    setToDate(undefined);

    setAppliedQuery({});
    setOffset(1);
  };

  const clearSingleFilter = (filterLabel: string) => {
    const newQuery = { ...appliedQuery };

    switch (filterLabel) {
      case 'City':
        setCityId('');
        delete newQuery.cityId;
        break;
      case 'Area':
        setAreaId('');
        delete newQuery.areaId;
        break;
      case 'Unit Type':
        setUnitTypeId(undefined);
        delete newQuery.unitTypeId;
        break;
      case 'Status':
        setStatusList([]);
        delete newQuery.status;
        break;
      case 'From':
        setFromDate(undefined);
        delete newQuery.fromDate;
        break;
      case 'To':
        setToDate(undefined);
        delete newQuery.toDate;
        break;
    }

    setAppliedQuery(newQuery);
    setOffset(1);
    load(1, limit, newQuery);
  };

  const openMaintenanceModal = (item: ReservationsOperationsItem) => {
    setSelectedMaintenanceItem(item);
    setMaintenanceDescription('');
    setMaintenanceModalOpen(true);
  };

  const closeMaintenanceModal = () => {
    if (submittingMaintenance) return;
    setMaintenanceModalOpen(false);
    setSelectedMaintenanceItem(null);
    setMaintenanceDescription('');
  };

  useEffect(() => {
    if (maintenanceModalOpen) {
      maintenanceTextareaRef.current?.focus();
    }
  }, [maintenanceModalOpen]);

  const submitMaintenance = async () => {
    if (!selectedMaintenanceItem || !maintenanceDescription.trim()) {
      return;
    }

    const promise = createReservationOperationMaintenance({
      description: maintenanceDescription.trim(),
      reservationId: selectedMaintenanceItem.id
    }).then((response) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      closeMaintenanceModal();
      return response;
    });

    setSubmittingMaintenance(true);

    birdToast.promise(promise, {
      loadingMessage: 'Creating maintenance request...',
      successMessage: 'Maintenance request created successfully',
      errorPermissionMessage: 'You do not have permission to create maintenance request',
      errorFailedMessage: 'Failed to create maintenance request'
    });

    setSubmittingMaintenance(false);
  };

  // lookups (cities/areas/unit types/property managers)
  useEffect(() => {
    load(1, limit, {});
    getAllCity('operation-module').then((response: any) => {
      if (!isErrorInfo(response)) {
        setCitiesOptions(response?.map((c: any) => ({ value: c.id || '', label: c.name })));
      }
    });

    getAllAreas('operation-module').then((response: any) => {
      if (!isErrorInfo(response)) {
        setAreaOptions(response?.map((a: any) => ({ value: a.id || '', label: a.name })));
      }
    });

    getAllUnitTypes('reservation-module', 0, -1, {}).then(
      (response: UnitTypeUiModelResponse | any) => {
        if (!isErrorInfo(response)) {
          setUnitTypeOptions(
            response?.unitList?.map((u: any) => ({ value: Number(u.id), label: u.name || '' })) ||
              []
          );
        }
      }
    );
  }, []);

  return (
    <>
      <div className="min-h-screen bg-creams-50 px-6 py-4">
        <div className="flex items-center justify-between gap-4 mb-4">
          <span className="text-4xl font-recoleta font-semibold text-neutral-500">Operations</span>

          <div className="flex items-center gap-1 flex-wrap">
            {hasAppliedFilters &&
              filterTags.map((tag) => (
                <button
                  type="button"
                  key={`${tag.label}-${tag.value}`}
                  onClick={() => clearSingleFilter(tag.label)}
                  className="inline-flex items-center gap-1.5 text-sm text-primary-700 font-gilroy-medium bg-primary-50 border border-primary-200 px-3 py-1 rounded-full hover:bg-primary-100 hover:border-primary-300 transition-colors cursor-pointer"
                  aria-label={`Clear ${tag.label}: ${tag.value}`}
                >
                  <span className="text-neutral-500">{tag.label}:</span>
                  <span>{tag.value}</span>
                  <svg
                    className="w-3.5 h-3.5 ml-1 text-primary-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <title>Remove</title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              ))}
            {visibleColumns.length < COLUMN_CONFIG.length && (
              <span className="text-base text-neutral-500 font-gilroy-medium">
                {visibleColumns.length} of {COLUMN_CONFIG.length} columns
              </span>
            )}
            <SortDropdown
              value={sortBy}
              onChange={(newSortBy) => {
                setSortBy(newSortBy);
                const newQuery = { ...appliedQuery };
                if (newSortBy) {
                  newQuery.sortBy = newSortBy;
                } else {
                  delete newQuery.sortBy;
                }
                setAppliedQuery(newQuery);
                setOffset(1);
                load(1, limit, newQuery);
              }}
            />
            <ColumnVisibilityDropdown
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumn}
              onShowAll={showAllColumns}
            />
            <Button
              onClick={() => setFiltersModalOpen(true)}
              type={ButtonType.transparent}
              size={ButtonSize.small}
              classNameButton="p-[5px]"
            >
              <FilterIcon size="18px" />
            </Button>
          </div>
        </div>

        {loadingData ? (
          <LoadingData />
        ) : (
          <>
            <div className="w-full overflow-x-auto border border-gray-200 rounded-xl bg-white shadow-sm ">
              <table
                className="min-w-[1600px] w-full border-separate border-spacing-0"
                aria-label="Operations reservations table"
              >
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="[&>th]:text-lg [&>th]:font-medium [&>th]:py-2.5 [&>th]:font-gilroy-bold text-neutral-700 border-b border-gray-200">
                    {COLUMN_CONFIG.map(
                      ({ key, label, align = 'text-left' }) =>
                        visibleColumns.includes(key) && (
                          <th key={key} className={`px-3 ${align} whitespace-nowrap`} scope="col">
                            {label}
                          </th>
                        )
                    )}
                    <th className="px-3 text-left whitespace-nowrap" scope="col">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <Row
                      key={`${item.type}-${item.id}`}
                      item={item}
                      visibleColumns={visibleColumns}
                      onAddMaintenance={openMaintenanceModal}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {items.length === 0 && <NotFoundData />}
          </>
        )}

        {listCount > 0 && (
          <div className="fixed bottom-2 inset-x-0 mx-auto w-[90%] lg:w-[60%] flex justify-center items-center">
            <Pagination
              itemCounts={listCount}
              itemsPerPage={limit}
              offset={offset}
              handlePageChange={(newPage) => {
                setOffset(newPage);
                load(newPage, limit, appliedQuery);
              }}
              handleItemsPerPage={(itemsPerPage) => {
                setLimit(itemsPerPage);
                setOffset(1);
                load(1, itemsPerPage, appliedQuery);
              }}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        containerClassName="lg:h-fit lg:max-h-[80vh] lg:w-[850px]"
        headerContent={
          <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
            Filters
          </div>
        }
        headerAction={
          <Button
            onClick={() => {
              clearFilters();
              setFiltersModalOpen(false);
              load(1, limit, {});
            }}
            classNameButton="text-neutral-500 text-2xl"
            type={ButtonType.transparent}
          >
            Clear All
          </Button>
        }
        footerContent={
          <div className="flex justify-end gap-4">
            <Button
              onClick={() => {
                const newQuery = buildDraftQuery();
                setAppliedQuery(newQuery);
                setOffset(1);
                setFiltersModalOpen(false);
                load(1, limit, newQuery);
              }}
              size={ButtonSize.medium}
            >
              Apply
            </Button>
            <Button
              onClick={() => setFiltersModalOpen(false)}
              type={ButtonType.primaryInverted}
              size={ButtonSize.medium}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div className="relative flex flex-col flex-wrap mt-4 mb-4 px-4 py-4 md:flex-row md:py-6 md:pl-8 md:pr-2">
          <div className="w-full sm:w-1/2 md:w-1/3 px-2 mb-4">
            <div className="text-sm font-medium font-gilroy-medium text-neutral-500 mb-1">City</div>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              value={{
                value: cityId,
                label: citiesOptions?.find((c) => c.value === cityId)?.label || 'All'
              }}
              options={[{ value: '', label: 'All' }, ...citiesOptions]}
              classNamePrefix="react-select"
              onChange={(value: ValueType<{ value: string; label: string }, false>) =>
                setCityId(value ? value.value : '')
              }
            />
          </div>

          <div className="w-full sm:w-1/2 md:w-1/3 px-2 mb-4">
            <div className="text-sm font-medium font-gilroy-medium text-neutral-500 mb-1">Area</div>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              value={{
                value: areaId,
                label: areaOptions?.find((a) => a.value === areaId)?.label || 'All'
              }}
              options={[{ value: '', label: 'All' }, ...areaOptions]}
              classNamePrefix="react-select"
              onChange={(value: ValueType<{ value: string; label: string }, false>) =>
                setAreaId(value ? value.value : '')
              }
            />
          </div>

          <div className="w-full sm:w-1/2 md:w-1/3 px-2 mb-4">
            <div className="text-sm font-medium font-gilroy-medium text-neutral-500 mb-1">
              Unit Type
            </div>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              value={{
                value: unitTypeId ?? 0,
                label: unitTypeOptions?.find((u) => u.value === unitTypeId)?.label || 'All'
              }}
              options={[{ value: 0, label: 'All' }, ...unitTypeOptions]}
              classNamePrefix="react-select"
              onChange={(value: any) =>
                setUnitTypeId(value?.value ? Number(value.value) : undefined)
              }
            />
          </div>
          <div className="w-full sm:w-1/2 md:w-1/3 px-2 mb-4">
            <div className="text-sm font-medium font-gilroy-medium text-neutral-500 mb-1">
              Reservation Status
            </div>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              value={statusOptions.filter((opt) => statusList.includes(opt.value))}
              options={statusOptions}
              classNamePrefix="react-select"
              onChange={(values: any) => setStatusList((values || []).map((v: any) => v.value))}
            />
          </div>

          <div className="w-full md:w-2/3 px-2 mb-4">
            <div className="text-sm font-medium font-gilroy-medium text-neutral-500 mb-1">
              Date range
            </div>
            <div className="flex gap-2">
              <ReactDatePicker
                withPortal
                selected={fromDate}
                onChange={(date: Date) => {
                  setFromDate(date);
                  if (toDate && date && toDate.getTime() < date.getTime()) setToDate(undefined);
                }}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                placeholderText="From"
              />
              <ReactDatePicker
                withPortal
                selected={toDate}
                onChange={(date: Date) => setToDate(date)}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate}
                placeholderText="To"
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={maintenanceModalOpen}
        onClose={closeMaintenanceModal}
        containerClassName="lg:h-fit lg:max-h-[80vh] lg:w-[650px]"
        headerContent={
          <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
            Create New Maintenance
          </div>
        }
        footerContent={
          <div className="flex justify-end gap-4">
            <Button
              onClick={submitMaintenance}
              size={ButtonSize.medium}
              loading={submittingMaintenance}
              disabled={!maintenanceDescription.trim() || !selectedMaintenanceItem}
            >
              Send
            </Button>
            <Button
              onClick={closeMaintenanceModal}
              type={ButtonType.primaryInverted}
              size={ButtonSize.medium}
              disabled={submittingMaintenance}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div className="w-full">
          <div className="mb-2 text-2xl font-semibold font-gilroy-medium text-neutral-500">
            Description :
          </div>
          <textarea
            ref={maintenanceTextareaRef}
            value={maintenanceDescription}
            onChange={(event) => setMaintenanceDescription(event.target.value)}
            rows={5}
            placeholder="Write maintenance description"
            className="rounded-xl border border-gray-600 w-full px-4 py-3 text-xl text-neutral-600 focus:outline-primary-500 focus:border-primary-500"
          />
        </div>
      </Modal>
    </>
  );
}
